<template>
  <div
    class="images"
    :class="[
      $blockClass(blockClass, 'image-container'),
      paddingClass,
    ]"
    :style="{ paddingBlockEnd: aspectRatio }"
  >
    <img
      v-if="image && (image.src || image.url)"
      class="images__image"
      :class="[
        $blockClass(blockClass, 'image'),
        { 'lazyload': lazyload },
      ]"
      :alt="imageAlt"
      decoding="async"
      :role="role"
      :sizes="getSizes(false)"
      :src="getSrc(false)"
      :srcset="getSrcset(false).srcset"
      :data-sizes="getSizes(true)"
      :data-src="getSrc(true)"
      :data-srcset="getSrcset(true).srcset"
    >
  </div>
</template>

<script>

/**
 * Vue: Responsive image
 * -----------------------------------------------------------------------------
 * Base template for responsive images.
 * - image.src comes from formatted product response.
 * - image.url comes from unformatted cart.js response.
 *
 * @param {String} [alt] - Override image alt.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {Object} image - Image object formatted from Shopify response.
 * @param {String} [minMax] - Min and max size for srcset in `min-max` format.
 * @param {Boolean} [padding] - Add padding based on image ratio or supplied
 * ratio, default to true, disable to make image fill its parent container.
 * @param {String|Boolean} [ratio] - Aspect ratio of image in `width:height`
 * format or in decimal ratio format (width/height), will use original ratio of
 * image if not set or is false.
 * @param {String} [sizes] - Value for sizes attribute.
 *
 */
import { getSizedImageUrl } from '~/helpers/format'

export default {
  name: 'ResponsiveImage',

  props: {
    alt: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    image: {
      type: Object,
      default: () => ({}),
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    minMax: {
      type: String,
      default: '200-1000',
    },
    padding: {
      type: Boolean,
      default: true,
    },
    ratio: {
      type: [String, Boolean],
      default: '',
    },
    sizes: {
      type: String,
      default: 'auto',
    },
  },

  data() {
    return {
      global: true,
    }
  },

  computed: {

    /**
     * Compute aspect ratio.
     * @returns {Number|Boolean}
     */
    aspectRatio() {
      if (!this.padding) {
        return false
      }

      /**
       * If ratio is supplied without :, i.e. decimal ratio.
       */
      if (this.ratio && this.ratio.includes('.')) {
        return `${(1 / this.ratio) * 100.00}%`
      }

      /**
       * If no ratio supplied and image has width / height ratio in object.
       */
      if (!this.ratio && this.image.aspect_ratio) {
        return `${(1 / this.image.aspect_ratio) * 100.00}%`
      }

      let height = this.image.height * 100
      let width = this.image.width

      /**
       * If ratio is passed as prop.
       */
      if (this.ratio) {
        height = this.ratio.split(':')[1] * 100.00
        width = this.ratio.split(':')[0]
      }

      return `${height / width}%`
    },

    /**
     * Compute alt.
     * @returns {String}
     */
    imageAlt() {
      const imageAlt = this.alt || this.image.alt
      return imageAlt ? imageAlt : ''
    },

    /**
     * Compute image padding class.
     * @returns {String}
     */
    paddingClass() {
      if (this.padding) {
        return ''
      }

      return 'images--no-padding'
    },

    /**
     * Compute role.
     * @returns {String|Null}
     */
    role() {
      if (this.imageAlt) {
        return null
      }

      return 'presentation'
    },
  },

  methods: {

    /**
     * Get sizes attribute.
     * - Can either be sizes or data-sizes based on lazyload.
     * @param {Boolean} dataAttribute - Is data- attribute.
     * @returns {String|Null}
     */
    getSizes(dataAttribute) {
      if (this.lazyload && !dataAttribute) {
        return null
      }

      return this.sizes
    },

    /**
     * Get src URL.
     * - Can either be src or data-src based on lazyload.
     * @param {Boolean} dataAttribute - Is data- attribute.
     * @returns {String|Null}
     */
    getSrc(dataAttribute) {
      if (
        (!this.image.src && !this.image.url) ||
        (this.lazyload && !dataAttribute)
      ) {
        return null
      }

      return getSizedImageUrl(
        this.image.src || this.image.url,
        `${this.getSrcset().placeholder}x`,
      )
    },

    /**
     * Get srcset.
     * - Can either be srcset or data-srcset based on lazyload.
     * @param {Boolean} dataAttribute - Is data- attribute.
     * @returns {Object}
     */
    getSrcset(dataAttribute) {
      if (!this.image.src && !this.image.url) {
        return {
          srcset: null,
        }
      }

      const min = Number(this.minMax.split('-')[0])
      const max = Number(this.minMax.split('-')[1])

      const range = max - min
      const increment = range / 5

      const array = [...Array(5)].map((_, index) => {
        const step = increment * (index + 1)
        const value = min + step

        return value
      })

      const srcset = array.map((step) => {
        const roundedSize = Math.ceil(step)

        const sizedImageUrl = getSizedImageUrl(
          this.image.src || this.image.url,
          `${roundedSize}x`,
        )

        return `${sizedImageUrl} ${roundedSize}w`
      })

      return {
        array,
        max,
        min,
        placeholder: (min / 2),
        srcset: this.lazyload && !dataAttribute ? null : srcset,
      }
    },
  },
}
</script>
